import type { AnalyticsOrderSnapshot } from './analytics'
import { isAcceptedByCdek, type CustomerShipmentState } from './customer-state'
import { orderShipment } from './order-helpers'
import type { YMarketOrder } from './types'
import { CdekShipmentDirection, CdekStatusCode } from './cdek-domain'

const COMPLETED_SELLER_RETURN_CODES = new Set<string>([
  CdekStatusCode.ReturnedToSender,
  CdekStatusCode.ReturnedToClient,
])

const SELLER_RETURN_CODES = new Set<string>([
  CdekStatusCode.ReturnedToSenderCityWarehouse,
  ...COMPLETED_SELLER_RETURN_CODES,
])

function normalizedStatusCode(shipment?: CustomerShipmentState): string | undefined {
  return shipment?.statusCode?.trim().toUpperCase()
}

export function isExplicitSellerReturn(shipment?: CustomerShipmentState): boolean {
  const code = normalizedStatusCode(shipment)
  return Boolean(code && SELLER_RETURN_CODES.has(code))
}

export function isCompletedSellerReturn(shipment?: CustomerShipmentState): boolean {
  if (shipment?.direction === CdekShipmentDirection.Return) {
    // On a reverse waybill DELIVERED means the parcel reached the seller.
    // RETURNED_* means that reverse delivery itself failed and went back to
    // its sender (the buyer), so it is not a completed seller return.
    return shipment.state === 'delivered'
  }
  const code = normalizedStatusCode(shipment)
  return Boolean(code && COMPLETED_SELLER_RETURN_CODES.has(code))
}

export function isFailedReverseDeliveryStatus(statusCode?: string): boolean {
  const code = statusCode?.trim().toUpperCase()
  return code === CdekStatusCode.NotDelivered
    || code === CdekStatusCode.ReturnedToSender
    || code === CdekStatusCode.ReturnedToClient
}

export function reverseTrackingTarget(order: YMarketOrder): {
  orderId: number
  cdekNumber: string
} | null {
  const shipment = orderShipment(order)
  return shipment?.direction === CdekShipmentDirection.Return && shipment.cdekNumber
    ? { orderId: order.orderId, cdekNumber: shipment.cdekNumber }
    : null
}

/**
 * Market is the source of truth for a buyer cancellation. CDEK evidence is
 * used only to establish that the parcel had already entered logistics.
 */
export function marketCancellationStartedInDelivery(
  order: Pick<YMarketOrder, 'status' | 'statusBeforeCancellation' | 'programType'>,
  shipment?: CustomerShipmentState,
): boolean {
  if (order.status !== 'CANCELLED') return false
  if (order.statusBeforeCancellation === 'DELIVERY' || order.statusBeforeCancellation === 'PICKUP') return true
  if (order.programType && order.programType !== 'DBS') return false
  if (!shipment) return false
  if (isAcceptedByCdek(shipment.statusCode) || isExplicitSellerReturn(shipment)) return true
  return ['in_transit', 'delivery_issue', 'ready_for_pickup', 'out_for_delivery', 'delivered'].includes(shipment.state)
}

/** A Market-cancelled parcel that has not yet reached the seller. */
export function isMarketReturnInProgress(order: YMarketOrder): boolean {
  const shipment = orderShipment(order)
  if (!marketCancellationStartedInDelivery(order, shipment) || !shipment) return false
  const hasReverseLogisticsEvidence = shipment.direction === CdekShipmentDirection.Return
    || isExplicitSellerReturn(shipment)
  return hasReverseLogisticsEvidence && !isCompletedSellerReturn(shipment)
}

export function snapshotCancellationStartedInDelivery(
  snapshot: Pick<AnalyticsOrderSnapshot, 'status' | 'statusBeforeCancellation' | 'programType'>,
  shipment?: CustomerShipmentState,
): boolean {
  return marketCancellationStartedInDelivery(snapshot, shipment)
}
